import { getJson, sendJson } from '@/lib/api';

/**
 * "Perfect pairings" (Roy's portal list, item 14): two catalog programs BCE
 * recommends running together for an occasion, and why. Mirrors the API's
 * `program-pairings.service.ts` views.
 */

/** One side of a pairing. */
export interface PairingProgram {
  readonly id: string;
  /** Null when the program has left the catalog since the pairing was made. */
  readonly name: string | null;
  /** "Afternoon", "Evening"; empty when the pairing has no order. */
  readonly label: string;
  /** False when the program itself is inactive in the catalog. */
  readonly isActive: boolean;
}

/** A pairing, as `/program-pairings` sends it. */
export interface ProgramPairing {
  readonly id: string;
  readonly occasion: string;
  readonly reason: string;
  readonly isActive: boolean;
  readonly programA: PairingProgram;
  readonly programB: PairingProgram;
  readonly updatedAt: string;
}

/** What the manager's form saves. */
export interface ProgramPairingInput {
  readonly occasion: string;
  readonly programAId: string;
  readonly programALabel: string;
  readonly programBId: string;
  readonly programBLabel: string;
  readonly reason: string;
  readonly isActive: boolean;
}

/** A program the pickers offer. */
export interface ProgramOption {
  readonly id: string;
  readonly name: string;
  readonly isActive: boolean;
}

/** Field limits, matching the API's schema. */
export const PAIRING_LIMITS = { occasion: 120, label: 40, reason: 600 } as const;

const BASE = '/program-pairings';
/** The programs API's largest page. */
const PROGRAM_PAGE_SIZE = 200;

export const PROGRAM_PAIRINGS_QUERY_KEY = ['program-pairings'] as const;
export const PROGRAM_OPTIONS_QUERY_KEY = ['program-options'] as const;

export const listProgramPairings = (signal?: AbortSignal): Promise<ProgramPairing[]> =>
  getJson<ProgramPairing[]>(BASE, undefined, signal);

export const createProgramPairing = (input: ProgramPairingInput): Promise<ProgramPairing> =>
  sendJson<ProgramPairing>('post', BASE, input);

export const updateProgramPairing = (id: string, input: Partial<ProgramPairingInput>): Promise<ProgramPairing> =>
  sendJson<ProgramPairing>('patch', `${BASE}/${id}`, input);

export const removeProgramPairing = (id: string): Promise<void> => sendJson<void>('delete', `${BASE}/${id}`);

/**
 * The whole program catalog, for the pickers, by name. Pages through the
 * programs API so a catalog past one page is never cut short.
 *
 * @param signal - Abort signal.
 * @returns Every program.
 */
export async function listProgramOptions(signal?: AbortSignal): Promise<ProgramOption[]> {
  const options: ProgramOption[] = [];
  for (let page = 1; ; page += 1) {
    const rows = await getJson<{ _id: string; name?: string; isActive?: boolean }[]>(
      '/programs',
      { page, limit: PROGRAM_PAGE_SIZE },
      signal,
    );
    for (const row of rows) {
      options.push({ id: row._id, name: row.name ?? 'Untitled program', isActive: row.isActive !== false });
    }
    if (rows.length < PROGRAM_PAGE_SIZE) return options;
  }
}

/** A side's name as the manager shows it. */
export function pairingProgramName(program: PairingProgram): string {
  return program.name ?? 'Program no longer in the catalog';
}
